import { InvoiceReportsSearchRefetchProvider } from '@/components/InvoiceReportDetailDrawer/context/InvoiceReportsSearchRefetchContext';
import type { CheckPermissionFn } from '@/components/InvoiceReportDetailDrawer/type';
import { useViewAdminInvoiceReportDetail } from '@/features/admin_invoice_reports/components/AdminInvoiceReportDetailDrawer/hooks/useViewAdminInvoiceReportDetail';
import {
  ConfirmCancelModal,
  ConfirmCancelModalProvider,
  useConfirmCancelModal,
} from '@/features/admin_invoice_reports/components/ConfirmCancelModal';
import {
  ConfirmDeleteModalProvider,
  ConfirmSingleDeleteModal,
  useConfirmDeleteModal,
} from '@/features/admin_invoice_reports/components/ConfirmDeleteModal';

import type {
  AdminInvoiceReportColumn,
  AdminInvoiceReportsSearchQuery,
} from '@/features/admin_invoice_reports/types';
import { formatConfirmModalTotalAmount } from '@/features/admin_invoice_reports/utils/formatConfirmModalTotalAmount';
import { useTranslation } from '@/i18n';
import {
  ModernPaginationProps,
  Table,
  TableProps,
} from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, memo, useCallback, useMemo } from 'react';
import { AdminInvoiceReportDetailDrawer } from '../../AdminInvoiceReportDetailDrawer';
import { ConfirmBulkDeleteModal } from '../ConfirmBulkDeleteModal';
import { TriggerFindTargetReportProvider } from '../context/TriggerFindTargetReportContext';
import { getTotalTableWidth, useColumns } from './Columns';
import { useResizableColumns } from './hooks/useResizableColumns';
import { useTableHeight } from './hooks/useTableHeight';
import styles from './Table.module.scss';
import {
  isValidSortState,
  MultiColumnSortState,
  SortOrderState,
} from './types';

const cx = classnames.bind(styles);

// Stable default constants — prevent new function/array references on each render
const EMPTY_SELECTED_ROW_KEYS: string[] = [];
const EMPTY_SELECT_ALL = () => {};
const EMPTY_SELECT_ROW = () => {};
const EMPTY_BULK_DELETE_IDS: string[] = [];
const NOOP_BULK_DELETE_CLOSE = () => {};

type InternalTableProps = Omit<
  TableProps<AdminInvoiceReportColumn>,
  'columns' | 'pagination' | 'emptyType'
>;

type HandleSelectRowProps = {
  handleSelectAll?: (checked: boolean) => void;
  isAllSelected?: boolean;
  selectedRowKeys?: string[];
  handleSelectRow?: (
    record: AdminInvoiceReportColumn,
    checked: boolean
  ) => void;
  isAllDisabled?: boolean;
};

export type AdminInvoiceReportsTableProps = HandleSelectRowProps & {
  data: AdminInvoiceReportColumn[];
  pagination: ModernPaginationProps;
  isLoading?: boolean;
  isSearchEmpty?: boolean;
  sortOrders?: MultiColumnSortState[];
  onMultiColumnSort?: (sortStates: MultiColumnSortState[]) => void;
  onSearch?: (query: { page?: number }) => void;
  /**
   * Re-fetch the list query. Wired for:
   * - NA-4869 drawer 404 refresh
   * - NA-4736 single-row cancel success
   * - NA-4735 single-row delete success
   * - NA-3160 bulk-delete success
   */
  onListRefetch: CheckPermissionFn;
  bulkDeleteModalOpen?: boolean;
  bulkDeleteCount?: number;
  bulkDeleteIds?: string[];
  onCloseBulkDeleteModal?: () => void;
};

const useEmptyState = (isSearchEmpty: boolean) => {
  const { t } = useTranslation();
  return useMemo(() => {
    if (isSearchEmpty) {
      return {
        emptyType: 'search' as const,
        emptyMessage: t('admin_invoice_reports.table.no_search_results'),
      };
    }
    return {
      emptyType: 'object' as const,
      emptyMessage: t('admin_invoice_reports.table.empty_message'),
    };
  }, [isSearchEmpty, t]);
};

type InnerProps = AdminInvoiceReportsTableProps & {
  selectedId: string;
  onDrawerOpen: (id: string) => void;
  onDrawerClose: () => void;
  setSelectedIndex: (index: number | undefined) => void;
};

const AdminInvoiceReportsTableInner: FC<InnerProps> = memo(
  ({
    data,
    pagination,
    isLoading = false,
    isSearchEmpty = false,
    sortOrders,
    onMultiColumnSort,
    onSearch,
    selectedRowKeys = EMPTY_SELECTED_ROW_KEYS,
    handleSelectRow = EMPTY_SELECT_ROW,
    handleSelectAll = EMPTY_SELECT_ALL,
    isAllSelected = false,
    isAllDisabled = false,
    onListRefetch,
    bulkDeleteModalOpen = false,
    bulkDeleteCount = 0,
    bulkDeleteIds = EMPTY_BULK_DELETE_IDS,
    onCloseBulkDeleteModal = NOOP_BULK_DELETE_CLOSE,
    selectedId,
    onDrawerOpen,
    onDrawerClose,
    setSelectedIndex,
  }) => {
    const { onOpenConfirmCancelModal } = useConfirmCancelModal();
    const { onOpenConfirmDeleteModal } = useConfirmDeleteModal();
    const { columnWidths, handleResize } = useResizableColumns();

    const handleCancel = useCallback(
      (record: AdminInvoiceReportColumn) => {
        onOpenConfirmCancelModal({
          id: record.id,
          number: record.number,
          title: record.title,
          apPayeeName: record.ap_payee_name ?? null,
          totalAmount: formatConfirmModalTotalAmount(
            record.total_amount,
            'list'
          ),
        });
      },
      [onOpenConfirmCancelModal]
    );

    const handleDelete = useCallback(
      (record: AdminInvoiceReportColumn) => {
        onOpenConfirmDeleteModal({
          id: record.id,
          number: record.number,
          title: record.title,
          apPayeeName: record.ap_payee_name,
          totalAmount:
            record.total_amount === '-' ? null : record.total_amount ?? null,
        });
      },
      [onOpenConfirmDeleteModal]
    );

    const columns = useColumns({
      selectedRowKeys,
      onSelectAll: handleSelectAll,
      onSelectRow: handleSelectRow,
      isAllSelected,
      isAllDisabled,
      isLoading,
      columnWidths,
      sortOrders,
      onCancel: handleCancel,
      onDelete: handleDelete,
    });

    const { refContainer, listHeight } = useTableHeight({ marginHeight: 100 });
    const { emptyType, emptyMessage } = useEmptyState(isSearchEmpty);

    const onPageChange = useCallback(
      (page: number) => {
        onSearch?.({ page });
      },
      [onSearch]
    );

    const paginationWithOnChange = useMemo(
      () => ({ ...pagination, onChange: onPageChange }),
      [pagination, onPageChange]
    );

    const handleColumnResize = useCallback(
      (columnIndex: number, width: number) => {
        handleResize(columnIndex, width, columns);
      },
      [handleResize, columns]
    );

    const resizableProps = useMemo(
      () => ({ resizable: true as const, onResize: handleColumnResize }),
      [handleColumnResize]
    );

    const totalTableWidth = useMemo(
      () => getTotalTableWidth(columnWidths),
      [columnWidths]
    );

    const scrollY = `${listHeight}px`;
    const scroll = useMemo(
      () => ({ x: totalTableWidth, y: scrollY }),
      [totalTableWidth, scrollY]
    );

    const handleMultiColumnSortWrapper = useCallback(
      (sortStates: unknown) => {
        if (!onMultiColumnSort) return;
        const statesArray = Array.isArray(sortStates)
          ? sortStates
          : [sortStates];
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        const typedStates = statesArray as SortOrderState[];
        const converted: MultiColumnSortState[] = typedStates
          .filter(isValidSortState)
          .map((s) => ({ columnKey: s.columnKey, order: s.order }));
        onMultiColumnSort(converted);
      },
      [onMultiColumnSort]
    );

    const tableData: InternalTableProps = useMemo(
      () => ({
        data,
        onRow: (record, index) => ({
          onClick: () => {
            onDrawerOpen(record.id);
            if (typeof index === 'number') setSelectedIndex(index);
          },
          className: cx({
            [styles['selected-row']]:
              selectedId === record.id || selectedRowKeys.includes(record.id),
          }),
        }),
      }),
      [data, onDrawerOpen, selectedId, selectedRowKeys, setSelectedIndex]
    );

    const hasData = data.length > 0;
    const containerClassName = cx(
      styles['container'],
      hasData && styles['reports-has-data']
    );

    return (
      <>
        <div
          className={containerClassName}
          ref={refContainer}
          data-testid='admin-invoice-reports-table'
        >
          <Table
            emptyType={emptyType}
            {...tableData}
            legacy={false}
            key={pagination.currentPage}
            testId='admin-invoice-reports-table'
            emptyText={emptyMessage}
            columns={columns}
            pagination={paginationWithOnChange}
            hideHeaderPagination
            scroll={scroll}
            loading={isLoading}
            tableLayout='fixed'
            isMultiColumnSortEnabled
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
            onMultiColumnSort={handleMultiColumnSortWrapper as any}
            initialSortState={sortOrders}
            {...resizableProps}
          />
        </div>
        {selectedId && (
          <AdminInvoiceReportDetailDrawer
            open
            onClose={onDrawerClose}
            selectedId={selectedId}
            onListRefetch={onListRefetch}
          />
        )}
        <ConfirmSingleDeleteModal />
        {bulkDeleteModalOpen && (
          <ConfirmBulkDeleteModal
            bulkDeleteModalOpen={bulkDeleteModalOpen}
            bulkDeleteCount={bulkDeleteCount}
            bulkDeleteIds={bulkDeleteIds}
            onCloseBulkDeleteModal={onCloseBulkDeleteModal}
            refetch={onListRefetch}
            selectedId={selectedId || null}
            onDrawerClose={onDrawerClose}
          />
        )}
        <ConfirmCancelModal />
      </>
    );
  }
);

AdminInvoiceReportsTableInner.displayName = 'AdminInvoiceReportsTableInner';

type AdminInvoiceReportsTableWithSearchProps = AdminInvoiceReportsTableProps & {
  searchQuery: AdminInvoiceReportsSearchQuery;
};

export const AdminInvoiceReportsTable: FC<AdminInvoiceReportsTableWithSearchProps> =
  memo(
    ({
      onListRefetch,
      data,
      pagination: basePagination,
      isLoading = false,
      onSearch,
      searchQuery,
      ...rest
    }) => {
      const {
        selectedId,
        onDrawerOpen,
        onDrawerClose,
        selectedIndex,
        setSelectedIndex,
      } = useViewAdminInvoiceReportDetail();

      // Use raw URL page value to avoid the clamped currentPage from
      // useConvertToModernPagination. When the last item on page N is cancelled
      // and total_pages drops below N, Math.min(N, total_pages) = 1, making
      // currentPage === 1 and suppressing the "go back to page 1" navigation.
      const currentPage = searchQuery.page ?? 1;
      const pagination = useMemo(
        () => ({ ...basePagination, currentPage }),
        [basePagination, currentPage]
      );

      return (
        <InvoiceReportsSearchRefetchProvider value={onListRefetch}>
          <TriggerFindTargetReportProvider
            data={data}
            pagination={pagination}
            selectedIndex={selectedIndex}
            setSelectedIndex={setSelectedIndex}
            onSearch={onSearch ?? (() => {})}
            isDataLoading={isLoading}
          >
            <ConfirmCancelModalProvider
              refetch={onListRefetch}
              onDrawerClose={onDrawerClose}
              selectedId={selectedId || null}
            >
              <ConfirmDeleteModalProvider
                refetch={onListRefetch}
                onDrawerOpen={onDrawerOpen}
                onDrawerClose={onDrawerClose}
                selectedId={selectedId || null}
              >
                <AdminInvoiceReportsTableInner
                  {...rest}
                  data={data}
                  pagination={pagination}
                  isLoading={isLoading}
                  onSearch={onSearch}
                  onListRefetch={onListRefetch}
                  selectedId={selectedId}
                  onDrawerOpen={onDrawerOpen}
                  onDrawerClose={onDrawerClose}
                  setSelectedIndex={setSelectedIndex}
                />
              </ConfirmDeleteModalProvider>
            </ConfirmCancelModalProvider>
          </TriggerFindTargetReportProvider>
        </InvoiceReportsSearchRefetchProvider>
      );
    }
  );

AdminInvoiceReportsTable.displayName = 'AdminInvoiceReportsTable';
